import axios from 'axios';
import type { UnifiedTask } from './unifiedTasks';
import type { RunSummary } from './cafAutomation';

/**
 * API client for the Tasks Rebuild lane pages (Task 8+).
 *
 * Pinned contract (Task 7 implements the backend routes this file calls):
 *   GET /ui/lanes/<lane>        -- paginated slim task rows for a lane
 *   GET /ui/lanes/counts        -- pending counts for all four lane tabs
 *   GET /ui/lanes/client/groups -- sender-grouped rows for the client lane accordion
 *   GET /ui/lanes/task/<id>     -- single-task detail row (demo-findings fix,
 *     see task_lane_service.get_lane_task's docstring): real columns off
 *     email_tasks/archived_email_tasks only, no email_task_content
 *     dependency -- unlike GET /ui/tasks/<id>, which 404s on any DB where
 *     that content side-table's migration hasn't run yet even though the
 *     row exists.
 *   GET /ui/tasks/<id>/content  -- on-demand emailContent/claudeAnalysis fetch
 *     (already shipped -- see python-backend/app/routes/unified_tasks.py
 *     get_unified_task_content)
 *   GET /ui/team-tasks/team-members -- staff roster for the "Assign to
 *     staff" action (staff-assignment restoration fast-follow). Pre-existing
 *     route (app/routes/team_tasks.py, reused verbatim) -- not a `/ui/lanes`
 *     path, but grouped onto this same axios instance/module per the Task
 *     13 brief's "laneApi additions typed" instruction rather than standing
 *     up a separate client for two calls.
 *   POST /ui/team-tasks/bulk-assign -- assign (or, with `teamMember: null`,
 *     unassign) one or more tasks to a staff member. Pre-existing route,
 *     admin-only, extended to support the unassign case (see
 *     team_tasks.bulk_assign_tasks's docstring).
 *
 * Follows the same axios-instance-per-module idiom as src/api/unifiedTasks.ts
 * (own instance + auth/401 interceptors) rather than reusing another module's
 * instance, so this file has no import-time coupling to files another agent
 * may be editing concurrently.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export type LaneId = 'client' | 'lender' | 'post' | 'manual';

export const LANE_IDS: LaneId[] = ['client', 'lender', 'post', 'manual'];


/** The five Lender task lists (spec 2026-10-08 §4.12). These are the
 * backend's `worklist` values -- never shown to staff as they are. */
export type WorklistKey = 'final_response' | 'provisional' | 'needs_reply' | 'not_sure' | 'needs_checking';

/** `worklistCounts` on GET /ui/lanes/lender: one grouped SQL count per list
 * plus `all`. Absent on a backend that predates the lists -- the lane then
 * shows no chips rather than invented zeros. */
export interface WorklistCounts {
  final_response: number;
  provisional: number;
  needs_reply: number;
  not_sure: number;
  needs_checking: number;
  all: number;
}
/** Manual-lane task type, one per live Manual row (task_lane_service.MANUAL_KINDS). */
export type ManualTaskKind =
  | 'ringfence' | 'chase' | 'multi_rep_staff' | 'multi_rep_auto' | 'id_change' | 'staff';
/** Slim row shape returned by GET /ui/lanes/<lane> -- list-only fields, no
 * emailContent/claudeAnalysis (those live behind getTaskContent). */
export interface LaneTaskRow {
  id: string;
  workflow?: RunSummary | null;
  subject: string | null;
  email_from: string | null;
  clientName: string | null;
  assignedClaimId: string | null;
  received_date: string | null;
  createdAt: string | null;
  status: string | null;
  ai_category: string | null;
  ai_confidence: number | null;
  emailType: string | null;
  /** Staff assignment restoration (fast-follow) -- `null` when unassigned.
   * Always `null` on an archived row (see task_lane_service's
   * ARCHIVED_LIST_SELECT comment -- archived_email_tasks has no real column
   * to source this from). */
  assignedToTeamMember: string | null;
  /** Completed-task attribution (demo finding: "for completed emails we're
   * missing a lot of the details of who completed it and when it was
   * completed"). Real columns on BOTH email_tasks and archived_email_tasks
   * (task_lane_service.LANE_LIST_COLUMNS / ARCHIVED_LIST_SELECT) -- present
   * (possibly `null`) on every row regardless of `archived`. `completedBy`
   * is the raw actor string, including the CAF Backlog Processor's literal
   * `'CAF Backlog Processor'` marker for AI-completed tasks -- prettify at
   * render time, never in this layer. */
  completedBy: string | null;
  completedAt: string | null;
  /** Archive attribution -- only ever populated when this row came from the
   * `archived=true` path (task_lane_service.ARCHIVED_LIST_SELECT); `undefined`
   * (the key is simply absent) on a live row, never `null` vs `undefined`
   * ambiguity to worry about. `archivedBy` carries the CAF Backlog
   * Processor's own marker for AI-archived tasks, `'CAF_BACKLOG_PROCESSOR'`
   * (underscore -- distinct literal from completedBy's spaced form; both are
   * prettified to the same "CAF AI processor" label at render time). */
  archivedAt?: string | null;
  archivedBy?: string | null;
  archiveReason?: string | null;
  /** Whether the AI has classified this task yet (2026-08-10, aiProcessed
   * filter plan Task 2) -- collapsed server-side from the real
   * ai_processed_at timestamp (task_lane_service.list_lane) to a plain
   * boolean, present on every row regardless of the `aiProcessed` list
   * filter above being active. Backs the per-row "AI reviewed"/"Awaiting
   * AI" badge. */
  aiProcessed?: boolean;
  /** Persisted lender final-response flag (findability Phase 2) --
   * collapsed server-side from the tri-state BIT (NULL renders as false).
   * Backs the per-row "Final response" badge. */
  hasFinalResponse?: boolean;
  /** Informational committed Watching assessment; never the saved flag or filter. */
  aiSuspectsFinalResponse?: boolean;
  /** Raw email_source (70+ hold build) -- 'cancellation_ringfenced' backs
   * the manual lane's '70+' row badge. NULL on standard manual tasks and
   * always NULL on archived rows (no such column there). */
  email_source?: string | null;
  /** Manual-lane task type (Freshdesk #682), decided server-side by
   * task_lane_service.manual_task_kind -- the same rules as the Task type
   * filter. Only on live Manual-lane rows; absent elsewhere. Backs the
   * multi-rep "Sent by staff" / "Sent automatically" row label. */
  manualKind?: ManualTaskKind | null;
  /** Wrong-claim attach guard (2026-09-28): the row's own references, for
   * the bulk-assign confirm. `subjectClaimRefs` is subject-only -- the list
   * never reads the body. `null` AI ref on archived rows (no column). */
  aiClaimReference?: string | null;
  subjectClaimRefs?: string[];
  /** Lender task lists (P1). All optional: a backend that predates the lists
   * omits every key. `worklist: null` means the email is on no list (a
   * workflow would handle it). */
  worklist?: WorklistKey | null;
  /** Plain-English reason written by the backend, shown on the row. */
  worklistNote?: string | null;
  /** The underlying hold or gate code -- for reporting only, never shown. */
  worklistReasonCode?: string | null;
  /** Calendar date 'YYYY-MM-DD' (reply-by, FOS deadline or objection date).
   * Never a timestamp: format it with worklists.formatDueDate, never new Date(). */
  worklistDueDate?: string | null;
  /** The claim the AI would attach this email to (spec §4.10). */
  aiSuggestedClaimId?: string | null;
  aiSuggestedClaimRef?: string | null;
  /** Who attached `assignedClaimId`: 'ai' only once the AI switch is On. */
  claimLinkSource?: 'ai' | 'staff' | null;
  /** Server-computed Final response badge: the staff verdict when staff have
   * judged the email (a staff "Not a final response" turns it off), else
   * worklist = final_response. When present it alone decides the badge; the
   * old has_final_response BIT can hold keyword-era values. */
  finalResponseBadge?: boolean;
  /** Final review C4 (asked of the backend): true when the row is on Final
   * response only because the AI thinks so ("please confirm"), not verified
   * against the letter and not confirmed by staff. When absent, the lane
   * falls back to the list's reason code (worklists.finalResponseMarker). */
  finalResponseCheckNeeded?: boolean;
}

/** GET /ui/team-tasks/team-members roster entry. `pendingCount` is the
 * member's current live workload (status pending/in_progress tasks
 * assigned to them) -- staff-assignment fast-follow #1: the assign-to-staff
 * picker surfaces this so staff can see who's already loaded up before
 * adding more. Optional only so array literals in existing test mocks that
 * predate this field keep type-checking; the live endpoint always sends it. */
export interface TeamMember {
  name: string;
  email: string;
  pendingCount?: number;
}

/** Pending/Completed counts for the active tab/filters -- Task 11 (John's
 * ask: the status-toggle buttons should read "Pending (61,204)" /
 * "Completed (18,853)" and move with every active filter, incl. the
 * since-01/07/2026 chip). Both buckets are computed by the same
 * statusCounts companion query the backend's Task 10 work added
 * (task_lane_service.py), so they always reflect the SAME filter set as
 * `total` -- just split by status instead of collapsed into one number. */
export interface LaneStatusCounts {
  pending: number;
  completed: number;
}

export interface LaneTasksResponse {
  tasks: LaneTaskRow[];
  total: number;
  page: number;
  pageSize: number;
  /** Optional so a stale/older backend response (or a mocked test fixture
   * that predates this field) never breaks the type -- LaneShell already
   * treats `undefined` as "omit the parenthetical", never as zero. */
  statusCounts?: LaneStatusCounts;
  /** Lender lane only, from a lists-aware backend (null on other lanes). */
  worklistCounts?: WorklistCounts | null;
}

export interface LaneListParams {
  statusTab?: 'pending' | 'completed';
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  receivedFrom?: string;
  /** The omnibox's `until:` chip (#4) -- ISO `YYYY-MM-DD`, end-of-day
   * inclusive of that day on the backend (task_lane_service.py's
   * `received_date < receivedTo + 1 day`). No-op on the archived path,
   * same as receivedFrom. */
  receivedTo?: string;
  senderEmail?: string;
  claim?: string;
  assigned?: string;
  q?: string;
  archived?: boolean;
  /** Filter by whether the AI has classified the task yet.
   *  Omitted (the default) means no filter -- the list is unchanged. */
  aiProcessed?: 'all' | 'processed' | 'unprocessed';
  workflow?: 'only' | 'hide';
  /** Lender task list. Omitted means All, so an untouched lane sends the
   * same request as before the lists existed. */
  worklist?: WorklistKey | 'all';
  /** Manual lane's Task type dropdown (Freshdesk #682) -- 'chase' |
   * 'multi_rep' | 'id_change' | 'staff' | 'ringfence' (70+), plus the retired
   * 'standard' for saved links. Omitted means no filter; the backend
   * whitelists and ignores unknown values. */
  taskSource?: string;
}

export interface LaneCounts {
  client: number;
  lender: number;
  post: number;
  manual: number;
}

export interface LaneClientGroup {
  sender: string;
  displayName: string | null;
  clientName: string | null;
  taskCount: number;
  oldestReceived: string | null;
  newestReceived: string | null;
}

export interface LaneClientGroupsResponse {
  groups: LaneClientGroup[];
  total: number;
  /** Task 15 (John Lowry follow-up): pending/completed bucket counts under
   * the same non-status filters (assigned/senderEmail/search) this
   * endpoint applies -- the grouped accordion's own list is inherently
   * pending-only, but this field answers the same "how many pending vs
   * completed match today's filters" question `LaneTasksResponse.
   * statusCounts` does for flat mode, so the Pending/Completed toggle
   * buttons can show real numbers while grouped too. Optional for the same
   * reason `LaneTasksResponse.statusCounts` is -- a stale/older backend or
   * mocked test fixture that predates this field. */
  statusCounts?: LaneStatusCounts;
}

export interface LaneClientGroupsParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  /** #408 client lane assignment scoping: team member name, 'unassigned',
   * or the literals 'me'/'all' (see task_lanes._resolve_assigned_param).
   * Omitted means no filter (back-compat -- this endpoint had no
   * `assigned` support at all before this feature). */
  assigned?: string;
  /** Item 1b (lane-feedback batch 28/07): the omnibox's from: chip --
   * narrows the accordion to the matching sender's group(s). Omitted means
   * no filter (back-compat -- this endpoint had no senderEmail support at
   * all before this feature). */
  senderEmail?: string;
}

/** Full GET /ui/team-tasks/team-members response -- the staff-assignment
 * fast-follow's roster fetch already returns more than the `members`
 * array (getTeamMembers, below, only ever surfaced that slice for the
 * existing AssignedFilterDropdown/MemberPickerModal callers). #408 client
 * lane assignment scoping needs `currentUserTeamName` -- the signed-in
 * user's TEAM_MEMBERS roster name (team_tasks.py's mapping from
 * g.current_user_email) -- to compute the "N belong to other staff" bulk
 * warning under the All-staff view, without re-deriving that mapping
 * client-side. */
export interface TeamRoster {
  members: TeamMember[];
  unassignedCount: number;
  currentUser: string;
  currentUserTeamName: string | null;
  isTeamMember: boolean;
}

/** Shape of GET /ui/tasks/<id>/content -- both fields are `null` (not a 404)
 * when the task has no content row (e.g. manual tasks, or an unknown id). */
export interface TaskContent {
  emailContent: Record<string, any> | null;
  claudeAnalysis: Record<string, any> | null;
  /** Every claim ref the email quotes (subject + body), found server-side
   * with the CAF classifier's regex. Wrong-claim attach guard, 2026-09-28. */
  referencedClaimRefs?: string[];
}

function buildParams(params: Record<string, unknown>): URLSearchParams {
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qp.append(key, String(value));
  });
  return qp;
}

export const laneApi = {
  async getLaneTasks(lane: LaneId, params: LaneListParams = {}): Promise<LaneTasksResponse> {
    const qp = buildParams(params as Record<string, unknown>);
    const response = await axiosInstance.get<LaneTasksResponse>(`/ui/lanes/${lane}?${qp}`);
    return response.data;
  },

  async getLaneCounts(): Promise<LaneCounts> {
    const response = await axiosInstance.get<LaneCounts>('/ui/lanes/counts');
    return response.data;
  },

  async getClientGroups(params: LaneClientGroupsParams = {}): Promise<LaneClientGroupsResponse> {
    const qp = buildParams(params as Record<string, unknown>);
    const response = await axiosInstance.get<LaneClientGroupsResponse>(`/ui/lanes/client/groups?${qp}`);
    return response.data;
  },

  /** Single-task detail row -- demo-findings fix. Deliberately NOT
   * unifiedTasksApi.getTask: that route depends on the email_task_content
   * side-table existing (see the file-doc note above), which turns into a
   * false 404 on a DB where that migration hasn't run yet even though the
   * row is right there in email_tasks/archived_email_tasks. Typed as
   * UnifiedTask so TaskDetailPane needs no type changes -- fields this
   * endpoint doesn't select (emailContent, claudeAnalysis, ...) are simply
   * absent/undefined on the returned object. */
  async getLaneTask(taskId: string): Promise<UnifiedTask> {
    const response = await axiosInstance.get<UnifiedTask>(`/ui/lanes/task/${taskId}`);
    return response.data;
  },

  async getTaskContent(taskId: string): Promise<TaskContent> {
    const response = await axiosInstance.get<TaskContent>(`/ui/tasks/${taskId}/content`);
    return response.data;
  },

  /** Staff roster for the "Assign to staff" action. Any authenticated user
   * can fetch this -- it's also how the picker's dropdown gets populated.
   *
   * `lane`, when supplied, scopes each member's `pendingCount` to that lane
   * via the route's `?lane=` param (team_tasks.get_team_members ->
   * task_lane_service.member_pending_counts(lane)) -- fixes the
   * MemberPickerModal/AssignedFilterDropdown reviewer blocker where the
   * picker's "Erin — N pending" summed her workload across all four lanes
   * instead of scoping to the lane the picker was opened from. Omitting
   * `lane` preserves the original cross-lane total. The roster itself
   * (which names are returned) is never filtered by lane -- every
   * TEAM_MEMBERS entry always comes back, with `pendingCount: 0` for a
   * member with no pending work in that lane, so no one becomes unpickable
   * just because their queue in this lane happens to be empty. */
  async getTeamMembers(lane?: LaneId): Promise<TeamMember[]> {
    const qp = buildParams({ lane });
    const response = await axiosInstance.get<{ members: TeamMember[] }>(`/ui/team-tasks/team-members?${qp}`);
    return response.data.members;
  },

  /** Full roster response, including `currentUserTeamName` -- #408 client
   * lane assignment scoping's Mine/Unassigned/All-staff segmented control
   * needs the signed-in user's own roster name to compute the "N belong
   * to other staff" bulk-action warning under the All-staff view. Same
   * endpoint as getTeamMembers above, just not narrowed to `.members` --
   * see that method's doc for what `lane` does to `pendingCount`. */
  async getTeamRoster(lane?: LaneId): Promise<TeamRoster> {
    const qp = buildParams({ lane });
    const response = await axiosInstance.get<TeamRoster>(`/ui/team-tasks/team-members?${qp}`);
    return response.data;
  },

  /** Assign (or, with `member: null`, unassign) one or more tasks to a staff
   * member. Any authenticated staff may assign; server validates the member
   * against TEAM_EMAILS and stamps assignedToTeamMemberBy for audit. */
  async assignMember(taskIds: string[], member: string | null): Promise<{ assigned: number }> {
    const response = await axiosInstance.post<{ assigned: number }>('/ui/team-tasks/bulk-assign', {
      taskIds,
      teamMember: member,
    });
    return response.data;
  },
};
